import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, ChevronLeft, ChevronRight, Camera, Edit3, Sparkles, Eye, BarChart3, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DashboardTutorialModal = ({ isOpen, onClose, token }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const slideDuration = 5000; // 5 seconds per slide

  const slides = [
    {
      id: 1,
      title: "Welcome to Your Dashboard!",
      description: "Let's take a quick tour to help you get started with tracking your nutrition.",
      icon: Upload,
      color: "from-primary to-emerald-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center">
                <Camera className="w-7 h-7 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg">Upload Your Meals</h4>
                <p className="text-sm text-white/70">Tap the + buttons to add meals, snacks, or drinks</p>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <div className="px-4 py-2 bg-primary rounded-full text-white text-sm font-medium">+ Add Meal</div>
              <div className="px-4 py-2 bg-secondary rounded-full text-white text-sm font-medium">+ Add Snack</div>
              <div className="px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium border border-white/30">+ Add Drink</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "Edit Your Food Entry",
      description: "After uploading, you can edit the food description to add or modify items.",
      icon: Edit3,
      color: "from-blue-500 to-cyan-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <p className="text-sm text-white/60 mb-2">Food Description</p>
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  value="Grilled chicken with rice and vegetables" 
                  readOnly
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Edit3 className="w-4 h-4 text-blue-400" />
                </div>
              </div>
            </div>
            <p className="text-sm text-white/70 text-center">
              Click the edit icon to modify the food contents before AI analysis
            </p>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "Run AI Analysis",
      description: "Submit your meal for AI-powered nutritional analysis to get detailed insights.",
      icon: Sparkles,
      color: "from-purple-500 to-pink-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <div className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-bold">
                Run AI Analysis
              </div>
              <p className="text-sm text-white/70 text-center">
                Our AI will analyze your meal photo and calculate all nutrients automatically
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "View Macronutrients",
      description: "See your carbs, protein, fat, and sugar at a glance on each food card.",
      icon: BarChart3,
      color: "from-amber-500 to-orange-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-blue-500/20 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-blue-400">45g</p>
                <p className="text-xs text-white/60">Carbs</p>
              </div>
              <div className="bg-green-500/20 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-green-400">32g</p>
                <p className="text-xs text-white/60">Protein</p>
              </div>
              <div className="bg-yellow-500/20 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-yellow-400">12g</p>
                <p className="text-xs text-white/60">Fat</p>
              </div>
              <div className="bg-pink-500/20 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-pink-400">8g</p>
                <p className="text-xs text-white/60">Sugar</p>
              </div>
            </div>
            <p className="text-sm text-white/70 text-center">
              Macros are displayed on every food entry card
            </p>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: "Explore Micronutrients",
      description: "Click on any food image to see detailed micronutrients like vitamins and minerals.",
      icon: Eye,
      color: "from-teal-500 to-cyan-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-teal-500/30 to-cyan-500/30 flex items-center justify-center border-2 border-dashed border-white/30">
                  <Camera className="w-10 h-10 text-white/50" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-teal-400 font-bold">Vitamin A</p>
                <p className="text-white/60">120%</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-teal-400 font-bold">Iron</p>
                <p className="text-white/60">45%</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-teal-400 font-bold">Calcium</p>
                <p className="text-white/60">30%</p>
              </div>
            </div>
            <p className="text-sm text-white/70 text-center mt-4">
              Tap the food image to open the detail view with all micronutrients
            </p>
          </div>
        </div>
      )
    },
    {
      id: 6,
      title: "You're All Set!",
      description: "Start tracking your meals to build healthy habits one bite at a time.",
      icon: Sparkles,
      color: "from-primary to-emerald-500",
      content: (
        <div className="space-y-4">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center mb-4">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h4 className="text-xl font-bold mb-2">Ready to Start!</h4>
            <p className="text-white/70 mb-4">
              You now know how to upload meals, edit entries, run AI analysis, and view your nutritional breakdown.
            </p>
            <p className="text-sm text-white/50">
              Close this tutorial and add your first meal!
            </p>
          </div>
        </div>
      )
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isOpen) return;
    
    if (isPlaying) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            // Move to next slide
            setCurrentSlide((current) => {
              if (current >= slides.length - 1) {
                setIsPlaying(false);
                return current;
              }
              return current + 1;
            });
            return 0;
          }
          return prev + (100 / (slideDuration / 100));
        });
      }, 100);
      
      timerRef.current = interval;
      return () => clearInterval(interval);
    }
  }, [isOpen, isPlaying, slides.length]);

  // Reset progress when slide changes
  useEffect(() => {
    setProgress(0);
  }, [currentSlide]);

  const handleClose = async () => {
    // Mark tutorial as seen
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/complete-dashboard-tutorial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Failed to mark tutorial as complete:', error);
    }
    onClose();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      setProgress(0);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
      setProgress(0);
    }
  };

  if (!isOpen) return null;

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br ${currentSlideData.color}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center transition-colors"
            data-testid="close-tutorial-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Progress Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-black/20">
            <div className="flex gap-1 h-full px-4 pt-1">
              {slides.map((slide, index) => (
                <div key={`progress-${slide.title}-${index}`} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all duration-100"
                    style={{ 
                      width: index < currentSlide ? '100%' : index === currentSlide ? `${progress}%` : '0%' 
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8 pt-12 text-white">
            {/* Slide Counter */}
            <div className="text-center mb-2">
              <span className="text-sm text-white/60">{currentSlide + 1} / {slides.length}</span>
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <currentSlideData.icon className="w-8 h-8 text-white" />
              </div>
            </div>

            {/* Title & Description */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-6"
              >
                <h3 className="text-2xl font-bold mb-2">{currentSlideData.title}</h3>
                <p className="text-white/80">{currentSlideData.description}</p>
              </motion.div>
            </AnimatePresence>

            {/* Slide Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                {currentSlideData.content}
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  currentSlide === 0 ? 'bg-white/10 text-white/30' : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
              </button>

              {currentSlide === slides.length - 1 ? (
                <Button
                  onClick={handleClose}
                  className="rounded-full px-6 py-2 bg-white text-gray-900 hover:bg-white/90 font-bold"
                  data-testid="get-started-tutorial-btn"
                >
                  Get Started!
                </Button>
              ) : (
                <button
                  onClick={nextSlide}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Skip Button */}
            <div className="text-center mt-4">
              <button
                onClick={handleClose}
                className="text-sm text-white/60 hover:text-white/80 underline"
                data-testid="skip-tutorial-btn"
              >
                Skip tutorial
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DashboardTutorialModal;
